// src/features/clients/ClientsTab.jsx
import React, { useState, useEffect } from "react";
import { useClients } from "./hooks/useClients";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import { Search, Users, ArrowRightLeft, Edit, Trash2 } from "lucide-react";
import ClientsMigrationTool from "./temp/ClientsMigrationTool";
import EditClientModal from "./components/EditClientModal";

function ClientsTab() {
  const { clients, isLoading, fetchClients, deleteClient, addClient } =
    useClients();
  const [searchTerm, setSearchTerm] = useState("");
  const [showMigration, setShowMigration] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm) ||
      (client.cpf && client.cpf.includes(searchTerm))
  );

  const handleUpdateClient = async (data) => {
    const success = await addClient(data);
    if (success) {
      fetchClients();
      setEditingClient(null);
    }
  };

  if (showMigration) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => {
            setShowMigration(false);
            fetchClients();
          }}
        >
          ← Voltar para Lista
        </Button>
        <ClientsMigrationTool />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Users className="h-6 w-6" />
          Clientes
          {/* ✅ CONTADOR DE CLIENTES ADICIONADO AQUI */}
          {!isLoading && (
            <span className="ml-2 text-sm font-normal text-muted-foreground bg-gray-100 px-2.5 py-0.5 rounded-full dark:bg-gray-800 border">
              {clients.length}
            </span>
          )}
        </h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowMigration(true)}>
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Importar/Migrar Dados
          </Button>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, telefone ou CPF..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        {/* Opcional: Mostrar contador de resultados da busca se estiver filtrando */}
        {searchTerm && (
          <span className="text-sm text-muted-foreground">
            Encontrados: {filteredClients.length}
          </span>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Cidade</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  Nenhum cliente encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="font-medium">{client.name}</TableCell>
                  <TableCell>{client.phone}</TableCell>
                  <TableCell>{client.cpf || "-"}</TableCell>
                  <TableCell>{client.city || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingClient(client)}
                      >
                        <Edit className="h-4 w-4 text-muted-foreground" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => deleteClient(client.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editingClient && (
        <EditClientModal
          isOpen={!!editingClient}
          onClose={() => setEditingClient(null)}
          onSave={handleUpdateClient}
          clientData={editingClient}
        />
      )}
    </div>
  );
}

export default ClientsTab;
